import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, Clock } from 'lucide-react';
import {
  ONE_TIME_PACKAGES,
  PROMO_EXPIRY_ISO,
  PROMO_EXPIRY_DISPLAY,
  SEAT_PROMO_CODE,
  SEAT_PROMO_RATE,
  LAUNCH_PROMO_CODE,
  LAUNCH_PROMO_RATE,
  LAUNCH_PROMO_CAP,
  FALLBACK_PROMO_CODE,
  FALLBACK_PROMO_RATE,
} from '../data/constants';
import { trackEvent } from '../utils/metaTracking';

const PROMO_EXPIRY = new Date(PROMO_EXPIRY_ISO);

// The ad offer applies to every package; prices shown here come from the shared
// package catalog so this page can never drift from what checkout charges.
const PACKAGES = ONE_TIME_PACKAGES.filter((p) => p.id !== 'dev_test');

const money = (n) => (Number.isInteger(n) ? `${n}` : n.toFixed(2));
const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;
const ADVANCED = PACKAGES.find((p) => p.id === 'interior');
const CAR_SEAT_CENTS = 3500;

function useCountdown(target) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, target - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const days = Math.floor(timeLeft / 86400000);
  const hours = Math.floor((timeLeft % 86400000) / 3600000);
  const mins = Math.floor((timeLeft % 3600000) / 60000);
  const secs = Math.floor((timeLeft % 60000) / 1000);
  return { days, hours, mins, secs, expired: timeLeft === 0 };
}

const TimeBlock = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="bg-white border-2 border-[#68caf1]/30 rounded-xl px-4 py-3 min-w-[60px] text-center shadow-sm">
      <span className="text-3xl font-black text-[#111827]">{String(value).padStart(2, '0')}</span>
    </div>
    <span className="text-[10px] text-gray-400 uppercase tracking-widest mt-1.5">{label}</span>
  </div>
);

export default function PromoPage() {
  const { days, hours, mins, secs, expired } = useCountdown(PROMO_EXPIRY.getTime());

  // The launch offer closes automatically after LAUNCH_PROMO_CAP bookings.
  // Check the live count: if the spots are gone, this page swaps to the
  // fallback offer (10% off through the promo end date) instead of sending
  // people to a dead code. If the check fails, the page just shows the
  // standard offer.
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/promo-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data?.launch) setStatus(data.launch); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const soldOut = !!status?.soldOut;

  // Meta ViewContent: one event per landing (the ad click -> page view signal).
  useEffect(() => {
    trackEvent('ViewContent', {
      content_name: `${LAUNCH_PROMO_CODE} promo`,
      content_category: 'promo_landing',
      content_type: 'product',
    });
  }, []);

  const activeCode = soldOut ? FALLBACK_PROMO_CODE : LAUNCH_PROMO_CODE;
  const discountRate = soldOut ? FALLBACK_PROMO_RATE : LAUNCH_PROMO_RATE;
  const pct = Math.round(discountRate * 100);
  const bookingUrl = (packageId) => `/detail/book?package=${packageId}&promo=${activeCode}`;
  const afterOff = (price) => price * (1 - discountRate);

  // The stacked example: Advanced sedan + car seat with BOTH codes. Same integer
  // arithmetic as checkout, so the page can never promise a different number.
  const pctInt = Math.round(discountRate * 100);
  const stackPkgCents = ADVANCED.priceCents - Math.round((ADVANCED.priceCents * pctInt) / 100);
  const stackSeatCents = CAR_SEAT_CENTS - Math.round((CAR_SEAT_CENTS * Math.round(SEAT_PROMO_RATE * 100)) / 100);
  const stackTotalCents = stackPkgCents + stackSeatCents;
  const stackDepositCents = Math.round(stackTotalCents / 2);
  const stackedUrl = `/detail/book?package=${ADVANCED.id}&addons=car_seat&promo=${activeCode}&seatpromo=${SEAT_PROMO_CODE}`;
  const seatOnlyUrl = `/detail/book?package=${ADVANCED.id}&addons=car_seat&seatpromo=${SEAT_PROMO_CODE}`;

  return (
    <div className="min-h-screen bg-gray-50 text-[#111827]">

      {/* Logo */}
      <div className="text-center pt-10 pb-6">
        <img
          src="/images/logo.png"
          alt="Monarch Detailing"
          className="h-20 w-auto mx-auto object-contain"
        />
      </div>

      {/* Hero */}
      <section className="px-6 pb-10 text-center">
        <div className="inline-block bg-[#68caf1]/10 border border-[#68caf1]/40 text-[#0e87b3] px-4 py-1.5 rounded-full text-xs font-bold tracking-[0.25em] uppercase mb-5">
          Limited Time Offer
        </div>
        {soldOut ? (
          <>
            <h1 className="text-5xl md:text-7xl font-black mb-4 leading-none tracking-tight text-[#111827]">
              The first {LAUNCH_PROMO_CAP} spots<br />
              <span className="text-[#68caf1]">are gone 🙏</span>
            </h1>
            <p className="text-gray-500 text-lg max-w-md mx-auto leading-relaxed mb-3">
              The {Math.round(LAUNCH_PROMO_RATE * 100)}% off spots went fast — but you're not leaving empty-handed. Take <strong className="text-[#111827]">{pct}% off any package</strong> with code <strong className="text-[#111827]">{FALLBACK_PROMO_CODE}</strong>, through {PROMO_EXPIRY_DISPLAY}.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-5xl md:text-7xl font-black mb-4 leading-none tracking-tight text-[#111827]">
              {pct}% Off<br />
              <span className="text-[#68caf1]">Any Interior Package</span>
            </h1>
            <p className="text-gray-500 text-lg max-w-md mx-auto leading-relaxed mb-3">
              Professional mobile interior detailing — Akeem comes to your home or job. No driving required.
            </p>
            {status && status.remaining !== null && (
              <p className="text-red-500 text-sm font-bold mb-3">
                🔥 Only {status.remaining} of {LAUNCH_PROMO_CAP} spots left
              </p>
            )}
          </>
        )}
        <p className="inline-block bg-white border-2 border-dashed border-[#68caf1] rounded-xl px-5 py-2 mb-4 text-sm text-gray-600">
          Use code <strong className="text-[#111827] font-black tracking-widest text-base">{activeCode}</strong> — applied automatically when you book
        </p>
        <p className="text-[#0e87b3] text-sm font-semibold">📍 Southern Maryland · St. Mary's · Charles · Calvert County</p>
      </section>

      {/* Countdown */}
      {!expired ? (
        <section className="px-6 mb-10">
          <div className="max-w-sm mx-auto bg-white border border-gray-200 rounded-2xl p-6 text-center shadow-sm">
            <div className="flex items-center justify-center gap-1.5 text-[#0e87b3] text-xs font-bold uppercase tracking-widest mb-4">
              <Clock size={13} />
              Offer expires in
            </div>
            <div className="flex items-center justify-center gap-3">
              <TimeBlock value={days} label="Days" />
              <span className="text-gray-300 text-2xl font-bold pb-4">:</span>
              <TimeBlock value={hours} label="Hrs" />
              <span className="text-gray-300 text-2xl font-bold pb-4">:</span>
              <TimeBlock value={mins} label="Min" />
              <span className="text-gray-300 text-2xl font-bold pb-4">:</span>
              <TimeBlock value={secs} label="Sec" />
            </div>
          </div>
        </section>
      ) : (
        <section className="px-6 mb-10 text-center">
          <p className="text-red-500 font-semibold">This offer has expired.</p>
        </section>
      )}

      {/* Primary CTA */}
      <section className="px-6 mb-10">
        <a
          href="#packages"
          className="block w-full max-w-md mx-auto bg-[#68caf1] text-black text-center font-black text-xl py-5 rounded-2xl hover:bg-[#5bb8db] transition-all shadow-[0_4px_24px_rgba(104,202,241,0.35)]"
        >
          {soldOut ? `Pick a Package — ${pct}% Off with ${FALLBACK_PROMO_CODE}` : `Claim ${pct}% Off — Pick Your Package`}
          <span className="block text-sm font-semibold mt-1 opacity-70">Discount auto-applied at checkout</span>
        </a>
      </section>

      {/* The offers */}
      <section className="px-6 mb-10 max-w-lg mx-auto">
        <p className="text-[#0e87b3] text-xs font-bold uppercase tracking-widest mb-4 text-center">The Offers · All End {PROMO_EXPIRY_DISPLAY}</p>
        <div className="space-y-3">
          <div className={`bg-white rounded-2xl p-4 shadow-sm border-2 ${soldOut ? 'border-gray-200 opacity-60' : 'border-[#68caf1]'}`}>
            <p className="font-black text-[#111827]">{LAUNCH_PROMO_CODE} <span className="font-semibold text-[#0e87b3]">— {Math.round(LAUNCH_PROMO_RATE * 100)}% off any package</span></p>
            <p className="text-gray-500 text-sm mt-1">First {LAUNCH_PROMO_CAP} bookings only.{soldOut ? ' Sold out — see FB10.' : ''}</p>
          </div>
          <div className={`bg-white rounded-2xl p-4 shadow-sm border-2 ${soldOut ? 'border-[#68caf1]' : 'border-gray-200'}`}>
            <p className="font-black text-[#111827]">{FALLBACK_PROMO_CODE} <span className="font-semibold text-[#0e87b3]">— {Math.round(FALLBACK_PROMO_RATE * 100)}% off any package</span></p>
            <p className="text-gray-500 text-sm mt-1">Everyone, once the first {LAUNCH_PROMO_CAP} {LAUNCH_PROMO_CODE} spots are gone.</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border-2 border-gray-200">
            <p className="font-black text-[#111827]">{SEAT_PROMO_CODE} <span className="font-semibold text-[#0e87b3]">— {Math.round(SEAT_PROMO_RATE * 100)}% off the car seat add-on</span></p>
            <p className="text-gray-500 text-sm mt-1">Car Seat Deep Clean {dollars(stackSeatCents)} (normally {dollars(CAR_SEAT_CENTS)}) with any detail. <strong>Stacks with {LAUNCH_PROMO_CODE} or {FALLBACK_PROMO_CODE}.</strong></p>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="px-6 mb-10 max-w-lg mx-auto scroll-mt-6">
        <p className="text-[#0e87b3] text-xs font-bold uppercase tracking-widest mb-4 text-center">Choose Your Package · {pct}% Off Applied</p>
        <div className="space-y-5">
          {PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white rounded-2xl p-6 shadow-sm border-2 ${pkg.highlight ? 'border-[#68caf1]' : 'border-gray-200'}`}
            >
              <div className="flex items-start justify-between gap-3 mb-1">
                <p className="text-xl font-black">{pkg.emoji} {pkg.name}</p>
                {pkg.highlight && (
                  <span className="bg-[#68caf1] text-black text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest whitespace-nowrap">Most Popular</span>
                )}
              </div>
              <p className="text-gray-500 text-sm mb-4">{pkg.tagline}</p>
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                {[
                  { label: 'Sedan', was: pkg.price },
                  { label: 'SUV', was: pkg.suvPrice },
                  { label: 'Truck', was: pkg.truckPrice },
                ].map(({ label, was }) => (
                  <div key={label} className="bg-gray-50 rounded-xl py-2">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wider">{label}</p>
                    <p className="text-gray-300 line-through text-xs">${was}</p>
                    <p className="font-black text-[#111827]">${money(afterOff(was))}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-2 mb-4">
                {pkg.features.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#68caf1]/15 border border-[#68caf1]/40 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={11} className="text-[#0e87b3]" />
                    </div>
                    <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              {pkg.notIncluded && pkg.notIncluded.length > 0 && (
                <p className="text-gray-400 text-xs mb-4">Not included: {pkg.notIncluded.join(' · ')}</p>
              )}
              <Link
                to={bookingUrl(pkg.id)}
                className={`flex items-center justify-center gap-2 w-full text-center font-black text-base py-4 rounded-2xl transition-all ${
                  pkg.highlight ? 'bg-[#68caf1] text-black hover:bg-[#5bb8db]' : 'bg-gray-900 text-white hover:bg-gray-700'
                }`}
              >
                Book {pkg.name} <ChevronRight size={18} />
              </Link>
            </div>
          ))}
        </div>
        <a
          href="sms:+13018859767"
          onClick={() => trackEvent('Contact', { content_name: 'promo_text_to_book' })}
          className="mt-5 flex items-center justify-between gap-3 bg-white border-2 border-gray-200 hover:border-[#68caf1] rounded-2xl p-4 transition-all"
        >
          <span>
            <span className="block font-black text-[#111827] text-sm">Rather text? Text Akeem to book</span>
            <span className="block text-gray-500 text-xs mt-0.5">Tell him the package and a day — he'll set it up with you.</span>
          </span>
          <span className="font-black text-[#0e87b3] text-sm whitespace-nowrap">(301) 885-9767</span>
        </a>

        <div className="mt-5 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <p className="text-[#0e87b3] text-xs font-bold uppercase tracking-widest mb-3">Offer Details</p>
          <ul className="space-y-2 text-gray-600 text-sm leading-relaxed list-disc pl-5">
            <li><strong>50% deposit due today</strong> to reserve your slot; the balance is due at service.</li>
            <li>Mobile service only in <strong>St. Mary's, Charles &amp; Calvert County, MD</strong> — Akeem comes to you.</li>
            <li>
              <strong>{activeCode}</strong> {soldOut ? `takes ${pct}% off` : `takes ${pct}% off any one-time interior package (limited to the first ${LAUNCH_PROMO_CAP} bookings)`} and does <strong>not</strong> apply to the monthly Interior Maintenance / Alchemist Reset program.
            </li>
            <li>Offer ends <strong>{PROMO_EXPIRY_DISPLAY}</strong>. One package code ({LAUNCH_PROMO_CODE} or {FALLBACK_PROMO_CODE}) per booking — and {SEAT_PROMO_CODE} (car seat add-on only) <strong>can be added on top</strong>.</li>
          </ul>
        </div>
      </section>

      {/* Real work — our own before/after */}
      <section className="px-6 mb-10 max-w-lg mx-auto">
        <p className="text-[#0e87b3] text-xs font-bold uppercase tracking-widest mb-3 text-center">Real Work, Real Interiors</p>
        <div className="grid grid-cols-2 gap-3">
          <figure>
            <img src="/images/beforeafter/before_interior.jpg" alt="Interior before a Monarch detail" className="rounded-xl w-full aspect-square object-cover" loading="lazy" />
            <figcaption className="text-center text-xs text-gray-500 mt-1.5 font-semibold uppercase tracking-widest">Before</figcaption>
          </figure>
          <figure>
            <img src="/images/beforeafter/after_interior.jpg" alt="Interior after a Monarch detail" className="rounded-xl w-full aspect-square object-cover" loading="lazy" />
            <figcaption className="text-center text-xs text-gray-500 mt-1.5 font-semibold uppercase tracking-widest">After</figcaption>
          </figure>
        </div>
        <p className="text-gray-400 text-xs text-center mt-3">Photos from Akeem's own work. Results vary with your vehicle's condition.</p>
      </section>

      {/* Stack the car seat special */}
      <section className="px-6 mb-10 max-w-lg mx-auto">
        <div className="bg-white border-2 border-[#68caf1] rounded-2xl p-6 shadow-sm text-center">
          <p className="text-[#0e87b3] text-xs font-bold uppercase tracking-widest mb-3">Parents — Stack the Savings</p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {SEAT_PROMO_CODE} works <strong>together</strong> with {activeCode}. The package discount and the car seat discount both apply.
          </p>
          <div className="bg-gray-50 rounded-xl p-4 mb-5 text-sm text-left space-y-1.5">
            <div className="flex justify-between text-gray-600"><span>Advanced (sedan) {dollars(ADVANCED.priceCents)} − {pctInt}% ({activeCode})</span><span className="font-semibold">{dollars(stackPkgCents)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Car Seat Deep Clean {dollars(CAR_SEAT_CENTS)} − 50% ({SEAT_PROMO_CODE})</span><span className="font-semibold">{dollars(stackSeatCents)}</span></div>
            <div className="flex justify-between font-black text-[#111827] border-t border-gray-200 pt-2 mt-1"><span>Total</span><span>{dollars(stackTotalCents)}</span></div>
            <div className="flex justify-between text-[#0e87b3] font-bold"><span>Deposit due today (50%)</span><span>{dollars(stackDepositCents)}</span></div>
          </div>
          <Link
            to={stackedUrl}
            className="flex items-center justify-center gap-2 w-full bg-[#68caf1] text-black text-center font-black text-base py-4 rounded-2xl hover:bg-[#5bb8db] transition-all mb-3"
          >
            Book Advanced + Car Seat with both codes <ChevronRight size={18} />
          </Link>
          <Link
            to={seatOnlyUrl}
            className="block text-center text-sm font-bold text-[#0e87b3] hover:underline"
          >
            Just the car seat special ({SEAT_PROMO_CODE} only)
          </Link>
        </div>
      </section>

      {/* Social proof */}
      <section className="px-6 mb-10 max-w-lg mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm text-center">
          <p className="text-gray-500 text-sm italic leading-relaxed mb-3">
            "Akeem transformed my car from a daily driver into something that looks showroom fresh. The attention to detail is incredible!"
          </p>
          <p className="text-[#0e87b3] text-xs font-semibold">— Sarah M., St. Mary's County</p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-6 pb-16">
        <a
          href="#packages"
          className="flex items-center justify-center gap-2 w-full max-w-md mx-auto bg-[#68caf1] text-black text-center font-black text-lg py-4 rounded-2xl hover:bg-[#5bb8db] transition-all shadow-[0_4px_24px_rgba(104,202,241,0.25)]"
        >
          {soldOut ? `Book Now — ${pct}% Off with ${FALLBACK_PROMO_CODE}` : `Book Now — ${pct}% Off`} <ChevronRight size={20} />
        </a>
        <p className="text-center text-gray-400 text-xs mt-4">
          Offer valid through {PROMO_EXPIRY_DISPLAY} · Southern Maryland only
        </p>
        <div className="text-center mt-8 text-xs text-gray-500 space-y-1">
          <p>Monarch Detailing · <a href="tel:+13018859767" className="text-[#0e87b3] hover:underline">(301) 885-9767</a> · <a href="mailto:info.monarchdetailing@gmail.com" className="text-[#0e87b3] hover:underline">info.monarchdetailing@gmail.com</a></p>
          <p><Link to="/privacy" className="text-[#0e87b3] hover:underline">Privacy Policy</Link> · <Link to="/terms" className="text-[#0e87b3] hover:underline">Terms of Service</Link></p>
        </div>
        <div className="text-center mt-6">
          <a href="https://monarchdetailing.art/monarch.vcf" className="text-[#0e87b3] text-sm font-medium hover:underline">
            📱 Save Akeem's Contact
          </a>
        </div>
      </section>

    </div>
  );
}
